import { useSyncExternalStore } from 'react'

import { DEFAULT_INDICATORS, type ManualIndicator } from '@/app/lib/quant/quantApi'
import { registerSessionReset } from '@/app/lib/sessionReset'

/**
 * The Manual trade indicator list (spec 036 Resolutions): module-level and
 * in-memory ONLY. It survives route and sub-tab switches (the module outlives
 * the page) and resets on a page reload. It is never written to storage.
 */
let list: ManualIndicator[] = DEFAULT_INDICATORS.map((i) => ({ ...i, params: { ...i.params } }))
const listeners = new Set<() => void>()

function emitChange(): void {
  for (const cb of listeners) cb()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

const getSnapshot = (): ManualIndicator[] => list

/** Replaces the list with `updater(current)`; a returned identical array is a no-op. */
export function updateQuantIndicators(updater: (current: ManualIndicator[]) => ManualIndicator[]): void {
  const next = updater(list)
  if (next === list) return
  list = next
  emitChange()
}

/** Switches one indicator on or off. */
export function toggleQuantIndicator(id: string): void {
  updateQuantIndicators((cur) => cur.map((i) => (i.id === id ? { ...i, on: !i.on } : i)))
}

/** Replaces an indicator's params, name and colour (the modal's Save & recompute). */
export function saveQuantIndicator(id: string, patch: Pick<ManualIndicator, 'params' | 'name' | 'color'>): void {
  updateQuantIndicators((cur) => cur.map((i) => (i.id === id ? { ...i, ...patch } : i)))
}

export function removeQuantIndicator(id: string): void {
  updateQuantIndicators((cur) => cur.filter((i) => i.id !== id))
}

/** A new id after the seeded `i1..i7`: `i<base36 time>`, made unique if the same millisecond repeats. */
export function newQuantIndicatorId(now: number = Date.now()): string {
  let id = `i${now.toString(36)}`
  let n = 1
  while (list.some((i) => i.id === id)) id = `i${now.toString(36)}${(n++).toString(36)}`
  return id
}

/** Appends a new indicator, switched on. */
export function addQuantIndicator(item: Omit<ManualIndicator, 'on'>): void {
  updateQuantIndicators((cur) => [...cur, { ...item, on: true }])
}

/** Back to the default seven. */
export function resetQuantIndicators(): void {
  list = DEFAULT_INDICATORS.map((i) => ({ ...i, params: { ...i.params } }))
  emitChange()
}

export interface QuantIndicatorsView {
  all: ManualIndicator[]
  /** Only the indicators switched on, in list order. */
  active: ManualIndicator[]
}

export function useQuantIndicators(): QuantIndicatorsView {
  const all = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return { all, active: all.filter((i) => i.on) }
}

registerSessionReset(resetQuantIndicators)
